import { m } from "@/paraglide/messages";
import { localizeHref } from "@/paraglide/runtime";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Link } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";
import { DeleteIcon } from "~/components/icons";
import { AlertDialogPro } from "~/components/ui/alert-dialog";
import { Button } from "~/components/ui/button";
import { PaginationPro } from "~/components/ui/pagination";
import { type Column, TablePro } from "~/components/ui/table-pro";
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "~/components/ui/tooltip";
import { BaseResponse } from "~/lib/request";
import { formatThousands, formatTimestampAuto } from "~/lib/utils";
import {
	CommentItem,
	CommentListResponse,
	commentList,
	deleteComment,
} from "./-api";

export const Route = createFileRoute("/$lang/profile/comment")({
	component: ProfileComment,
});

const PAGE_SIZE = 10;

function ProfileComment() {
	const [open, setOpen] = useState(false);
	const [selected, setSelected] = useState<string | null>(null);
	const [deleting, setDeleting] = useState(false);
	const [page, setPage] = useState(1);

	const queryClient = useQueryClient();

	const { data, isLoading } = useQuery<BaseResponse<CommentListResponse>>({
		queryKey: ["commentList", page],
		queryFn: () =>
			commentList({
				page_num: page,
				page_size: PAGE_SIZE,
			}),
	});

	const records: CommentListResponse["list"] = data?.data?.list ?? [];
	const total: number = data?.data?.total ?? 0;
	const pageCount = Math.ceil(total / PAGE_SIZE);

	const columns: Column<CommentItem>[] = [
		{
			key: "creation_title",
			title: m.profile_comment_creation_title(),
			className: "text-center max-w-[200px] truncate",
			render: (v, row) => (
				<Link
					to={localizeHref(`/story/${row.creation_id}/-`)}
					className="text-center max-w-[200px] truncate hover:underline"
				>
					{v}
				</Link>
			),
		},
		{
			key: "content",
			title: m.profile_comment_content(),
			width: 223,
			className: "text-center",
			render: (v, row) => (
				<Link
					to={localizeHref(`/story/${row.creation_id}/-`)}
					search={{ openComment: true }}
					className="line-clamp-3 break-words max-w-full text-wrap text-center hover:underline"
				>
					{v ?? "-"}
				</Link>
			),
		},
		{
			key: "likes",
			title: m.profile_comment_likes(),
			className: "text-center",
			render: (v) => <p className="text-center">{formatThousands(v ?? 0)}</p>,
		},
		{
			key: "created_time",
			title: m.profile_comment_time(),
			className: "text-center",
			render: (v) => <p className="text-center">{formatTimestampAuto(v)}</p>,
		},
		{
			key: "comment_id",
			title: m.common_operation(),
			className: "text-center",
			render: (_, row) => (
				<div className="flex justify-center">
					<Tooltip>
						<TooltipTrigger asChild>
							<Button
								className="w-6 h-6 rounded-full bg-bg2 hover:[&>svg>path]:stroke-w1"
								onClick={() => {
									setSelected(row.comment_id);
									setOpen(true);
								}}
							>
								<DeleteIcon className="[&>path]:stroke-t1" />
							</Button>
						</TooltipTrigger>
						<TooltipContent side="bottom">
							<p>{m.common_delete()}</p>
						</TooltipContent>
					</Tooltip>
				</div>
			),
		},
	];

	const handleConfirm = async () => {
		if (!selected) return;
		setDeleting(true);
		try {
			const res = await deleteComment(selected);
			if (res.code === 0) {
				toast.success(m.profile_comment_delete_success());
				queryClient.invalidateQueries({ queryKey: ["commentList"] });
			}
			setOpen(false);
		} finally {
			setDeleting(false);
		}
	};

	return (
		<div className="px-[22px] py-4 min-h-[570px]">
			<TablePro columns={columns} data={records} loading={isLoading} />

			{pageCount >= 1 && (
				<div className="flex justify-center py-4">
					<PaginationPro
						page={page}
						pageCount={pageCount}
						onPageChange={setPage}
					/>
				</div>
			)}

			<AlertDialogPro
				open={open}
				content={
					<div className="flex flex-col items-center gap-[6px]">
						<div className="mb-2 w-[120px] h-[120px]">
							<img
								className="w-full h-full object-cover"
								src="/assets/images/profile/creation.png"
								alt="creation"
							/>
						</div>
						<div className="text-t1 text-base font-normal">
							{m.profile_comment_delete_confirm()}
						</div>
					</div>
				}
				onCancel={() => setOpen(false)}
				onConfirm={handleConfirm}
				confirmText={
					deleting ? m.profile_comment_delete_loading() : m.common_confirm()
				}
				cancelText={m.common_cancel()}
				loading={deleting}
			/>
		</div>
	);
}
